import React from 'react';

import { ProductLicenseName } from 'core/constants';

import { LicenceNotice } from './styles';

const MULTI_GROUP_REFERENT = 'the groups you’ve added';

export enum LicenceNoticeSurface {
  AuditSchedule = 'auditSchedule',
  AssignmentRule = 'assignmentRule',
  DocumentAccess = 'documentAccess',
  AcknowledgementRequest = 'acknowledgementRequest',
}

interface SurfaceCopy {
  licenceKey: string;
  predicateAfterThey: string;
  outcomeClause: string;
}

const SURFACE_COPY: Record<LicenceNoticeSurface, SurfaceCopy> = Object.freeze({
  [LicenceNoticeSurface.AuditSchedule]: {
    licenceKey: 'workflows',
    predicateAfterThey: 'won’t be assigned these audits',
    outcomeClause: 'this schedule won’t assign any audits',
  },
  [LicenceNoticeSurface.AssignmentRule]: {
    licenceKey: 'events',
    predicateAfterThey: 'won’t be added as collaborators',
    outcomeClause: 'this rule won’t add any collaborators',
  },
  [LicenceNoticeSurface.DocumentAccess]: {
    licenceKey: 'documents',
    predicateAfterThey: 'won’t get access',
    outcomeClause: 'no one will get access',
  },
  [LicenceNoticeSurface.AcknowledgementRequest]: {
    licenceKey: 'documents',
    predicateAfterThey: 'won’t receive this request',
    outcomeClause: 'no one will receive this request',
  },
});

const licenceLabelFor = (licenceKey: string): string => {
  const name = ProductLicenseName[licenceKey];
  return `${/^[AEIOU]/.test(name) ? 'an' : 'a'} ${name} licence`;
};

interface LicenceCoverageNoticeProps {
  surface: LicenceNoticeSurface;
  total: number;
  licensed: number;
  groupNames: string[];
  groupSelectionChanged?: boolean;
  testId?: string;
}

const LicenceCoverageNotice: React.FunctionComponent<LicenceCoverageNoticeProps> = ({
  surface,
  total,
  licensed,
  groupNames,
  groupSelectionChanged = true,
  testId,
}) => {
  const missing = total - licensed;
  if (missing <= 0) return null;

  // Partial coverage is feedback on a change; nobody licensed is worth saying on every visit.
  if (licensed > 0 && !groupSelectionChanged) return null;

  const { licenceKey, predicateAfterThey, outcomeClause } = SURFACE_COPY[surface];
  const licenceLabel = licenceLabelFor(licenceKey);
  const oneGroup = groupNames.length === 1;
  const where = oneGroup ? groupNames[0] : MULTI_GROUP_REFERENT;

  const message =
    licensed === 0
      ? `No one in ${where} has ${licenceLabel}, so ${outcomeClause} until someone in ${
          oneGroup ? 'the group' : 'the groups'
        } gets one.`
      : `${missing} of ${total} users in ${where} ${
          missing === 1 ? 'doesn’t' : 'don’t'
        } have ${licenceLabel}, so they ${predicateAfterThey}.`;

  return (
    <LicenceNotice role="status" data-testid={testId}>
      {message}
    </LicenceNotice>
  );
};

export default LicenceCoverageNotice;
